import { FC, HTMLInputTypeAttribute, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { BUSINESS_TOOL_SECTIONS, BUSINESS_TOOL_SUB_SECTIONS } from 'constants/businessTool';
import { t } from 'i18next';

import { concatWithDot } from 'helpers/string';
import { ObjectValues } from 'helpers/types';

import { InitialAssetsForm } from '../InitialAssetsForm';
import { IInitialAssetsValuesType, initialAssetsInitialValues } from '../InitialAssetsForm/utils/form';
import { InitialEquityForm } from '../InitialEquityForm';
import { IInitialEquityValuesType, initialEquityInitialValues } from '../InitialEquityForm/utils/form';
import { InitialLiabilitiesForm } from '../InitialLiabilitiesForm';
import { IInitialLiabilitiesValuesType, initialLiabilitiesInitialValues } from '../InitialLiabilitiesForm/utils/form';

export const OPENING_BALANCE_SHEETS_TABS_NAMES = {
  INITIAL_ASSETS: 'initialAssets',
  INITIAL_LIABILITIES: 'initialLiabilities',
  INITIAL_EQUITY: 'initialEquity',
} as const;

export const IS_EQUAL = 'isEqual';

export const useGetOpeningBalanceSheetsTabs = () => {
  const { t, i18n } = useTranslation();
  return useMemo(() => {
    const OPENING_BALANCE_SHEETS_TABS = [
      {
        label: t('financialPlan.openingBalanceSheets.initialAssets.title'),
        value: OPENING_BALANCE_SHEETS_TABS_NAMES.INITIAL_ASSETS,
      },
      {
        label: t('financialPlan.openingBalanceSheets.initialLiabilities.title'),
        value: OPENING_BALANCE_SHEETS_TABS_NAMES.INITIAL_LIABILITIES,
      },
      {
        label: t('financialPlan.openingBalanceSheets.initialEquity.title'),
        value: OPENING_BALANCE_SHEETS_TABS_NAMES.INITIAL_EQUITY,
      },
    ];
    return OPENING_BALANCE_SHEETS_TABS;
  }, [t, i18n]);
};

export const openingBalanceSheetsPrefix = concatWithDot(
  BUSINESS_TOOL_SECTIONS.FINANCIAL_PLAN,
  BUSINESS_TOOL_SUB_SECTIONS.FINANCIAL_PLAN.OPENING_BALANCE_SHEETS,
);

export const initialAssetsFormPrefix = concatWithDot(
  openingBalanceSheetsPrefix,
  OPENING_BALANCE_SHEETS_TABS_NAMES.INITIAL_ASSETS,
);
export const initialLiabilitiesFormPrefix = concatWithDot(
  openingBalanceSheetsPrefix,
  OPENING_BALANCE_SHEETS_TABS_NAMES.INITIAL_LIABILITIES,
);
export const initialEquityFormPrefix = concatWithDot(
  openingBalanceSheetsPrefix,
  OPENING_BALANCE_SHEETS_TABS_NAMES.INITIAL_EQUITY,
);
export const isEqualFieldName = concatWithDot(openingBalanceSheetsPrefix, IS_EQUAL);

export type inputTypeProp = HTMLInputTypeAttribute | 'days';

export interface IQuestionEntity {
  question: string;
  endAdornment?: string | JSX.Element;
  startAdornment?: string | JSX.Element;
  inputType?: inputTypeProp;
  name: string;
  adornmentType?: 'currency' | 'percentage';
  maxValue?: number;
}

export type IOpeningBalanceSheetsValuesType = ObjectValues<typeof OPENING_BALANCE_SHEETS_TABS_NAMES>;

export const openingBalanceSheetsOptionsToComponentMap: Record<IOpeningBalanceSheetsValuesType, FC> = {
  [OPENING_BALANCE_SHEETS_TABS_NAMES.INITIAL_ASSETS]: InitialAssetsForm,
  [OPENING_BALANCE_SHEETS_TABS_NAMES.INITIAL_LIABILITIES]: InitialLiabilitiesForm,
  [OPENING_BALANCE_SHEETS_TABS_NAMES.INITIAL_EQUITY]: InitialEquityForm,
};

export interface IOpeningBalanceSheets {
  [OPENING_BALANCE_SHEETS_TABS_NAMES.INITIAL_ASSETS]: IInitialAssetsValuesType;
  [OPENING_BALANCE_SHEETS_TABS_NAMES.INITIAL_LIABILITIES]: IInitialLiabilitiesValuesType;
  [OPENING_BALANCE_SHEETS_TABS_NAMES.INITIAL_EQUITY]: IInitialEquityValuesType;
  [IS_EQUAL]: boolean;
}

export const openingBalanceSheetsInitialValues = {
  [OPENING_BALANCE_SHEETS_TABS_NAMES.INITIAL_ASSETS]: initialAssetsInitialValues,
  [OPENING_BALANCE_SHEETS_TABS_NAMES.INITIAL_LIABILITIES]: initialLiabilitiesInitialValues,
  [OPENING_BALANCE_SHEETS_TABS_NAMES.INITIAL_EQUITY]: initialEquityInitialValues,
  [IS_EQUAL]: false,
};

export const DEFAULT_DEBOUNCE_TIME_OPENING_BALANCE_SHEETS = 500;
